import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';

const Pagination = ({ 
  currentPage, 
  totalPages, 
  totalItems, 
  itemsPerPage, 
  onPageChange, 
  showItemsPerPage = false,
  onItemsPerPageChange = null,
  className = ""
}) => {
  const [goToPage, setGoToPage] = useState('');

  // Garantir que valores sejam números válidos
  const safeCurrentPage = Number(currentPage) || 1;
  const safeTotalPages = Number(totalPages) || 1;
  const safeTotalItems = Number(totalItems) || 0;
  const safeItemsPerPage = Number(itemsPerPage) || 10;

  const handleGoToPage = (e) => {
    e.preventDefault();
    const pageNum = parseInt(goToPage);
    if (pageNum >= 1 && pageNum <= safeTotalPages) {
      onPageChange(pageNum);
      setGoToPage('');
    }
  };
  // Calcular range de páginas a mostrar
  const getPageNumbers = () => {
    const delta = 2; // Quantas páginas mostrar antes e depois da atual
    const range = [];
    const rangeWithDots = [];

    // Se total de páginas é pequeno, mostrar todas
    if (safeTotalPages <= 7) {
      for (let i = 1; i <= safeTotalPages; i++) {
        range.push(i);
      }
      return range;
    }

    for (
      let i = Math.max(2, safeCurrentPage - delta);
      i <= Math.min(safeTotalPages - 1, safeCurrentPage + delta);
      i++
    ) {
      range.push(i);
    }

    if (safeCurrentPage - delta > 2) {
      rangeWithDots.push(1, '...');
    } else {
      rangeWithDots.push(1);
    }

    rangeWithDots.push(...range);

    if (safeCurrentPage + delta < safeTotalPages - 1) {
      rangeWithDots.push('...', safeTotalPages);
    } else if (safeTotalPages > 1) {
      rangeWithDots.push(safeTotalPages);
    }

    return rangeWithDots;
  };

  const pageNumbers = getPageNumbers();
  const startItem = safeTotalItems > 0 ? ((safeCurrentPage - 1) * safeItemsPerPage) + 1 : 0;
  const endItem = Math.min(safeCurrentPage * safeItemsPerPage, safeTotalItems);

  if (safeTotalPages <= 1) return null;

  return (
    <div className={`flex flex-col sm:flex-row items-center justify-between gap-4 ${className}`}>
      {/* Informações dos itens */}
      <div className="flex items-center gap-4">
        {/* Sem `totalItems` não dá para calcular o intervalo; mostrar
            "Mostrando 0 a 0 de 0 resultados" ao lado de uma tabela cheia
            confunde mais do que informa. Nesse caso exibimos só a página. */}
        <div className="text-sm text-gray-700">
          {safeTotalItems > 0 ? (
            <>
              Mostrando <span className="font-medium">{startItem}</span> a{' '}
              <span className="font-medium">{endItem}</span> de{' '}
              <span className="font-medium">{safeTotalItems}</span> resultados
            </>
          ) : (
            <>
              Página <span className="font-medium">{safeCurrentPage}</span> de{' '}
              <span className="font-medium">{safeTotalPages}</span>
            </>
          )}
        </div>
        
        {/* Seletor de itens por página */}
        {showItemsPerPage && onItemsPerPageChange && (
          <div className="flex items-center gap-2">
            <label htmlFor="itemsPerPage" className="text-sm text-gray-700">
              Por página:
            </label>
            <select
              id="itemsPerPage"
              value={safeItemsPerPage}
              onChange={(e) => onItemsPerPageChange(parseInt(e.target.value))}
              className="px-2 py-1 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-200 focus:border-blue-500"
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
        )}
      </div>

      {/* Controles de paginação — quebram em várias linhas no mobile em vez de
          serem cortados pela borda da tela */}
      <div className="flex flex-wrap items-center justify-center gap-2">
        {/* Primeira página */}
        <button
          onClick={() => onPageChange(1)}
          disabled={safeCurrentPage === 1}
          className="min-w-[40px] min-h-[40px] flex items-center justify-center text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          title="Primeira página"
        >
          <ChevronsLeft className="w-4 h-4" />
        </button>

        {/* Página anterior */}
        <button
          onClick={() => onPageChange(safeCurrentPage - 1)}
          disabled={safeCurrentPage === 1}
          className="min-w-[40px] min-h-[40px] flex items-center justify-center text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          title="Página anterior"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>

        {/* Números das páginas */}
        <div className="flex items-center gap-1 mx-2">
          {pageNumbers.map((page, index) => (
            <React.Fragment key={index}>
              {page === '...' ? (
                <span className="px-2 py-1 text-gray-500">...</span>
              ) : (
                <button
                  onClick={() => onPageChange(page)}
                  className={`px-3 py-2 text-sm font-medium rounded-md transition-colors min-w-[2.5rem] min-h-[40px] ${
                    safeCurrentPage === page
                      ? 'bg-blue-600 text-white shadow-sm'
                      : 'text-gray-700 hover:bg-gray-100 border border-gray-200'
                  }`}
                >
                  {page}
                </button>
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Próxima página */}
        <button
          onClick={() => onPageChange(safeCurrentPage + 1)}
          disabled={safeCurrentPage === safeTotalPages}
          className="min-w-[40px] min-h-[40px] flex items-center justify-center text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          title="Próxima página"
        >
          <ChevronRight className="w-4 h-4" />
        </button>

        {/* Última página */}
        <button
          onClick={() => onPageChange(safeTotalPages)}
          disabled={safeCurrentPage === safeTotalPages}
          className="min-w-[40px] min-h-[40px] flex items-center justify-center text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          title="Última página"
        >
          <ChevronsRight className="w-4 h-4" />
        </button>

        {/* Campo "Ir para página" */}
        <form onSubmit={handleGoToPage} className="flex items-center gap-2 sm:ml-4 sm:border-l sm:pl-4">
          <label htmlFor="goToPage" className="text-sm text-gray-700 whitespace-nowrap">
            Ir para:
          </label>
          <input
            id="goToPage"
            type="number"
            min="1"
            max={safeTotalPages}
            value={goToPage}
            onChange={(e) => setGoToPage(e.target.value)}
            placeholder={safeCurrentPage.toString()}
            className="w-16 px-2 py-1 border border-gray-300 rounded-md text-sm text-center focus:ring-2 focus:ring-blue-200 focus:border-blue-500"
          />
          <button
            type="submit"
            disabled={!goToPage || parseInt(goToPage) < 1 || parseInt(goToPage) > safeTotalPages}
            className="px-3 py-1 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Ir
          </button>
        </form>
      </div>
    </div>
  );
};

export default Pagination;
